import { t } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { GoPay, paths } from 'shopify-payment-core'

import { Brick } from '../../atoms/Brick'
import { logDebug } from '../../helpers/logger'
import { useFirebaseQuery } from '../../hooks/useFirebaseQuery'
import { i18n } from '../../i18n'
import {
  CredentialsStatus,
  ECredentialStatus,
} from '../../molecules/CredentialsStatus'
import { GoPayCredentialsForm } from './GoPayCredentialsForm'

interface IProps {
  accountId: ID
  useTestMode?: boolean
  shouldBeVerified?(datetime: string): boolean
}

export const GoPayCredentials = observer<IProps>(
  ({ accountId, useTestMode = false }) => {
    const credPath = paths.gopay.getAccountCredentials(accountId, useTestMode)
    const { pathRef: credRef, data: credentials } = useFirebaseQuery<
      GoPay.ICredentials
    >(credPath, { subscribe: true })

    const [verificationStatus, setVerificationStatus] = React.useState(
      ECredentialStatus.UNKNOWN,
    )

    React.useEffect(() => {
      if (credentials && credentials.verifiedAt) {
        setVerificationStatus(ECredentialStatus.VERIFIED)
      }
    })

    const onSubmit = React.useCallback(
      async (updatedCredentials: GoPay.ICredentials) => {
        setVerificationStatus(ECredentialStatus.VERIFYING)
        try {
          if (await verifyCredentials(updatedCredentials, useTestMode)) {
            await credRef.set({
              ...updatedCredentials,
              goId: updatedCredentials.goId.toString(),
              verifiedAt: new Date().toISOString(),
            })
            setVerificationStatus(ECredentialStatus.VERIFIED)
          } else {
            setVerificationStatus(ECredentialStatus.INVALID)
          }
        } catch (err) {
          setVerificationStatus(ECredentialStatus.UNKNOWN)
        }
      },
      [],
    )

    return (
      <Brick
        title={
          useTestMode
            ? i18n._(t`GoPay testovací údaje`)
            : i18n._(t`GoPay provozní údaje`)
        }
      >
        <GoPayCredentialsForm
          credentials={credentials}
          fieldIdSuffix={`${accountId}/${Number(useTestMode)}`}
          onSubmit={onSubmit}
        />
        <CredentialsStatus
          status={verificationStatus}
          verifiedAt={
            new Date(credentials ? credentials.verifiedAt! : Date.now())
          }
        />
      </Brick>
    )
  },
)

async function verifyCredentials(
  credentials: GoPay.ICredentials,
  testMode: boolean,
) {
  let url = 'https://gopay-verify.now.sh'
  if (testMode) {
    url += '?test=1'
  }

  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      body: JSON.stringify(credentials),
    })
    const { success } = await resp.json()
    return success
  } catch (err) {
    logDebug(err)
    return false
  }
}
